import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import FileUploader from "../../../../../components/shared/uploader/FileUploader";
import {
  MEHRSAN_STAGE_STEPS,
  STEP_STATUS,
  STEP_STATUS_LABELS,
} from "./loanMehrsanStageConstants";

const EDITABLE_STATUSES = [
  STEP_STATUS.PENDING,
  STEP_STATUS.IN_PROGRESS,
  STEP_STATUS.WAITING,
  STEP_STATUS.NEEDS_ACTION,
  STEP_STATUS.REVIEWING,
  STEP_STATUS.COMPLETED,
  STEP_STATUS.REJECTED,
];

const LoanMehrsanStepEditDialog = ({
  open,
  onClose,
  step,
  domain,
  fileBaseUrl = "",
  partners = [],
  saving = false,
  requiresLoanCreate = false,
  quickActionMode = false,
  onSave,
}) => {
  const [status, setStatus] = useState(STEP_STATUS.PENDING);
  const [referenceCode, setReferenceCode] = useState("");
  const [description, setDescription] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [attachmentName, setAttachmentName] = useState("");
  const [responsibleLabel, setResponsibleLabel] = useState("");
  const [lendingPartnerId, setLendingPartnerId] = useState("");

  const stepKey = step?.id ?? null;
  const isMehrsanQuick = quickActionMode && domain === "mehrsan";

  // Re-initialize only when the dialog opens or the target step changes.
  useEffect(() => {
    if (!open || !step) return;
    setStatus(step.status || STEP_STATUS.PENDING);
    setReferenceCode(step.referenceCode || "");
    setDescription(step.description || "");
    setAttachmentUrl(step.attachmentUrl || "");
    setAttachmentName(step.attachmentName || "");
    setResponsibleLabel(step.responsibleLabel || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stepKey]);

  useEffect(() => {
    if (!open) return;
    const iria = (partners || []).find(
      (p) => String(p?.code ?? p?.Code ?? "").toUpperCase() === "IRIA_PAY"
    );
    const defaultId =
      iria?.id ?? iria?.Id ?? partners[0]?.id ?? partners[0]?.Id ?? "";
    setLendingPartnerId((prev) => prev || defaultId);
  }, [open, partners]);

  const stepDef =
    domain === "loan"
      ? null
      : MEHRSAN_STAGE_STEPS.find((s) => s.id === step?.id);

  const actorLabel =
    domain === "loan"
      ? step?.actorLabel || step?.responsibleLabel || ""
      : stepDef?.responsibleLabel || "";

  const handleSave = (nextStatus = status) => {
    onSave?.({
      status: nextStatus,
      referenceCode: referenceCode.trim(),
      description: description.trim(),
      attachmentUrl: attachmentUrl.trim(),
      attachmentName: attachmentName.trim(),
      responsibleLabel: (responsibleLabel || actorLabel).trim(),
      actorLabel: (responsibleLabel || actorLabel).trim(),
      lendingPartnerId: requiresLoanCreate ? lendingPartnerId : null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{step?.title || "ثبت / ویرایش مرحله"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {requiresLoanCreate ? (
            <FormControl fullWidth size="small">
              <InputLabel id="lendtech-partner-label">لندتک</InputLabel>
              <Select
                labelId="lendtech-partner-label"
                label="لندتک"
                value={lendingPartnerId}
                onChange={(e) => setLendingPartnerId(e.target.value)}
              >
                {partners.map((p) => {
                  const id = p.id ?? p.Id;
                  return (
                    <MenuItem key={id} value={id}>
                      {p.title ?? p.Title ?? `لندتک ${id}`}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          ) : null}

          {quickActionMode ? (
            <Alert severity="info">
              {isMehrsanQuick
                ? "با «تأیید و مرحله بعد» این مرحله تکمیل می‌شود. توضیحات و فایل اختیاری است."
                : "برای ثبت اقدام فقط یکی از دکمه‌های «ادامه» یا «تأیید مرحله» را انتخاب کنید. توضیح و فایل اختیاری است."}
            </Alert>
          ) : (
            <>
              <FormControl fullWidth size="small">
                <InputLabel id="step-status-label">وضعیت</InputLabel>
                <Select
                  labelId="step-status-label"
                  label="وضعیت"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {EDITABLE_STATUSES.map((key) => (
                    <MenuItem key={key} value={key}>
                      {STEP_STATUS_LABELS[key]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                fullWidth
                label={domain === "loan" ? "کد رهگیری" : "کد رهگیری / مرجع"}
                value={referenceCode}
                onChange={(e) => setReferenceCode(e.target.value)}
              />

              <TextField
                size="small"
                fullWidth
                label={domain === "loan" ? "مسئول اقدام" : "مسئول"}
                value={responsibleLabel || actorLabel}
                onChange={(e) => setResponsibleLabel(e.target.value)}
                placeholder={actorLabel || undefined}
              />
            </>
          )}

          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label={quickActionMode ? "توضیحات (اختیاری)" : "توضیحات"}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <FileUploader
            moduleName="SLN"
            label={quickActionMode ? "فایل / مستند (اختیاری)" : "فایل / مستند"}
            accept="image/*,application/pdf"
            maxFiles={1}
            baseUrl={fileBaseUrl}
            value={attachmentUrl}
            onChange={(url) => {
              setAttachmentUrl(url || "");
              if (url) {
                const name = String(url).split("/").pop();
                if (name) setAttachmentName(name);
              }
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={saving}>
          انصراف
        </Button>
        {quickActionMode ? (
          isMehrsanQuick ? (
            <>
              <Button
                variant="outlined"
                startIcon={<PlayArrowIcon />}
                onClick={() => handleSave(STEP_STATUS.IN_PROGRESS)}
                disabled={saving}
              >
                در حال انجام
              </Button>
              <Button
                variant="contained"
                color="warning"
                startIcon={<SkipNextIcon />}
                onClick={() => handleSave(STEP_STATUS.COMPLETED)}
                disabled={saving}
              >
                {saving ? "در حال ثبت..." : "تأیید و مرحله بعد"}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<PlayArrowIcon />}
                onClick={() => handleSave(STEP_STATUS.IN_PROGRESS)}
                disabled={saving}
              >
                ادامه
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleOutlineIcon />}
                onClick={() => handleSave(STEP_STATUS.COMPLETED)}
                disabled={saving}
              >
                {saving ? "در حال ثبت..." : "تأیید مرحله"}
              </Button>
            </>
          )
        ) : (
          <Button variant="contained" onClick={() => handleSave()} disabled={saving}>
            {saving ? "در حال ذخیره..." : "ذخیره"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

LoanMehrsanStepEditDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  step: PropTypes.object,
  domain: PropTypes.oneOf(["mehrsan", "loan"]),
  fileBaseUrl: PropTypes.string,
  partners: PropTypes.array,
  saving: PropTypes.bool,
  requiresLoanCreate: PropTypes.bool,
  quickActionMode: PropTypes.bool,
  onSave: PropTypes.func,
};

export default LoanMehrsanStepEditDialog;
